import React from 'react';
import { Routes as RRDRoutes, Route, Navigate } from 'react-router-dom';
import {
  PermissionGate,
  Permissions,
  usePermission,
} from '@billda/permissions-ui';
import { FeatureSwitchGate, Features, useFeature } from '@billda/features-ui';
import NotFound from '../../../app/pages/NotFound';
import { Settings } from '../../../app/pages/Settings';
import SitesPage from '../../../features/sites/pages/SitesPage';
import SitePage from '../../../features/sites/pages/SitePage';
import SiteImportPage from '../../../features/sites/pages/SiteImportPage';

import LabourCataloguePage from '../../../features/labour/pages/LabourCataloguePage';
import VariationsPage from '../../../features/sites/pages/VariationsPage';
import ActivityPage from '../../../features/activity/pages/ActivityPage';
import UserManagementPage from '../../../features/users/pages/UserManagementPage';
import UserDetailPage from '../../../features/users/pages/UserDetailPage';
import InvitationLoadingPage from '../../../features/invitation/pages/InvitationLoadingPage';
import ProfileCapturePage from '../../../features/invitation/pages/ProfileCapturePage';
import DataSharingConsentPage from '../../../features/invitation/pages/DataSharingConsentPage';
import InvitationProcessingPage from '../../../features/invitation/pages/InvitationProcessingPage';
import BusinessDetailsCapturePage from '../../../features/invitation/pages/BusinessDetailsCapturePage';
import InvitationErrorPage from '../../../features/invitation/pages/InvitationErrorPage';
import InvitationDeclinedPage from '../../../features/invitation/pages/InvitationDeclinedPage';
import OrganizationSwitching from '../../../features/organisation/pages/OrganizationSwitching';
import AuthenticatedRoute from '../auth/AuthenticatedRoute';
import MagicLinkHandler from '../../../features/auth/pages/MagicLinkHandler';
import OffboardingPage from '../../../features/users/pages/OffboardingPage';
import GangManagementPage from '../../../features/gangs/pages/GangManagementPage';
import PotApprovalsPage from '../../../features/finance/pages/PotApprovalsPage';
import PotDetailsPage from '../../../features/finance/pages/PotDetailsPage';
import PaymentsPage from '../../../features/finance/pages/PaymentsPage';
import DeductionsPage from '../../../features/finance/pages/DeductionsPage';
import LoginPage from '../../../features/auth/pages/LoginPage';
import DownloadAppPage from '../../../app/pages/DownloadAppPage';
import { NotificationTestingPage } from '../../../features/development/pages/NotificationTestingPage';
import { ListMockupsPage } from '../../../features/development/mockups/ListMockupsPage';
import PlatformOrganisationsPage from '../../../features/platform/pages/PlatformOrganisationsPage';
import PlatformFeatureSwitchesPage from '../../../features/platform/pages/PlatformFeatureSwitchesPage';
import PlatformOrganisationDetailPage from '../../../features/platform/pages/PlatformOrganisationDetailPage';
import GettingStartedPage from '../../../features/onboarding/pages/GettingStartedPage';
import DashboardPage from '../../../features/dashboard/pages/DashboardPage';
import FilesPage from '../../../features/files/pages/FilesPage';
import { Routes as AppRoutes } from '../../../routes';
import { useAuthStore } from '../../../features/auth/stores/authStore';
import { useOnboardingStatus } from '../../../features/onboarding/hooks/useOnboardingStatus';

const RootRedirect: React.FC = () => {
  const { isAuthenticated } = useAuthStore();
  const hasPortalAccess = usePermission(Permissions.Portal.Access);
  const hasOrgManage = usePermission(Permissions.Organisation.Manage);
  const { data: onboardingStatus, isLoading } = useOnboardingStatus(
    isAuthenticated && hasOrgManage
  );
  const isDashboardEnabled = useFeature(Features.Dashboard.IsEnabled);

  if (!isAuthenticated) {
    return <Navigate to={AppRoutes.login} replace />;
  }

  if (!hasPortalAccess) {
    return <Navigate to={AppRoutes.downloadApp} replace />;
  }

  if (hasOrgManage && isLoading) {
    return null;
  }

  if (
    hasOrgManage &&
    onboardingStatus &&
    !onboardingStatus.gettingStartedCompletedAt
  ) {
    return <Navigate to={AppRoutes.onboarding.gettingStarted} replace />;
  }

  return (
    <Navigate
      to={isDashboardEnabled ? AppRoutes.dashboard : AppRoutes.sites.manage}
      replace
    />
  );
};

const AppRouter: React.FC = () => {
  return (
    <RRDRoutes>
      {/* Root redirect based on auth */}
      <Route path={AppRoutes.root} element={<RootRedirect />} />
      <Route path={AppRoutes.auth.magic} element={<MagicLinkHandler />} />
      <Route path={AppRoutes.login} element={<LoginPage />} />
      <Route
        path={AppRoutes.downloadApp}
        element={
          <AuthenticatedRoute>
            <DownloadAppPage />
          </AuthenticatedRoute>
        }
      />
      {/* Dashboard */}
      <Route
        path={AppRoutes.dashboard}
        element={
          <AuthenticatedRoute>
            <FeatureSwitchGate feature={Features.Dashboard.IsEnabled}>
              <DashboardPage />
            </FeatureSwitchGate>
          </AuthenticatedRoute>
        }
      />

      {/* Multi-page invitation flow */}
      <Route
        path={AppRoutes.invitation.base}
        element={<InvitationLoadingPage />}
      />
      <Route
        path={AppRoutes.invitation.profile}
        element={<ProfileCapturePage />}
      />
      <Route
        path={AppRoutes.invitation.consent}
        element={<DataSharingConsentPage />}
      />
      <Route
        path={AppRoutes.invitation.processing}
        element={<InvitationProcessingPage />}
      />
      <Route
        path="/invitation/:id/business-details"
        element={
          <AuthenticatedRoute>
            <BusinessDetailsCapturePage />
          </AuthenticatedRoute>
        }
      />
      <Route
        path={AppRoutes.invitation.error}
        element={<InvitationErrorPage />}
      />
      <Route
        path="/invitation/:id/declined"
        element={<InvitationDeclinedPage />}
      />

      {/* Error pages */}
      <Route
        path={AppRoutes.invitation.staticError}
        element={<InvitationErrorPage />}
      />
      <Route
        path={AppRoutes.invitation.staticDeclined}
        element={<InvitationDeclinedPage />}
      />
      <Route
        path={AppRoutes.organization.switching}
        element={
          <AuthenticatedRoute>
            <OrganizationSwitching />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/settings/*"
        element={
          <AuthenticatedRoute>
            <Settings />
          </AuthenticatedRoute>
        }
      />
      <Route
        path={AppRoutes.onboarding.gettingStarted}
        element={
          <AuthenticatedRoute>
            <PermissionGate permission={Permissions.Organisation.Manage}>
              <GettingStartedPage />
            </PermissionGate>
          </AuthenticatedRoute>
        }
      />
      <Route
        path={AppRoutes.sites.manage}
        element={
          <AuthenticatedRoute>
            <PermissionGate permission={Permissions.Sites.Read}>
              <SitesPage />
            </PermissionGate>
          </AuthenticatedRoute>
        }
      />
      <Route
        path={AppRoutes.sites.import}
        element={
          <AuthenticatedRoute>
            <PermissionGate permission={Permissions.Sites.Import}>
              <SiteImportPage />
            </PermissionGate>
          </AuthenticatedRoute>
        }
      />
      <Route
        path={AppRoutes.sites.detail}
        element={
          <AuthenticatedRoute>
            <SitePage />
          </AuthenticatedRoute>
        }
      />
      <Route
        path={AppRoutes.files.root}
        element={
          <AuthenticatedRoute>
            <PermissionGate permission={Permissions.Files.Read}>
              <FilesPage />
            </PermissionGate>
          </AuthenticatedRoute>
        }
      />
      <Route
        path={AppRoutes.files.site}
        element={
          <AuthenticatedRoute>
            <PermissionGate permission={Permissions.Files.Read}>
              <FilesPage />
            </PermissionGate>
          </AuthenticatedRoute>
        }
      />
      <Route
        path={AppRoutes.files.folder}
        element={
          <AuthenticatedRoute>
            <PermissionGate permission={Permissions.Files.Read}>
              <FilesPage />
            </PermissionGate>
          </AuthenticatedRoute>
        }
      />
      <Route
        path={AppRoutes.labour.manage}
        element={
          <AuthenticatedRoute>
            <PermissionGate permission={Permissions.Labour.Read}>
              <LabourCataloguePage />
            </PermissionGate>
          </AuthenticatedRoute>
        }
      />
      <Route
        path={AppRoutes.variations.manage}
        element={
          <AuthenticatedRoute>
            <PermissionGate permission={Permissions.Variations.Read}>
              <VariationsPage />
            </PermissionGate>
          </AuthenticatedRoute>
        }
      />
      <Route
        path={AppRoutes.activity.manage}
        element={
          <AuthenticatedRoute>
            <PermissionGate
              allOf={[Permissions.Sites.All, Permissions.Labour.All]}
            >
              <FeatureSwitchGate feature={Features.WorkActivity.IsEnabled}>
                <ActivityPage />
              </FeatureSwitchGate>
            </PermissionGate>
          </AuthenticatedRoute>
        }
      />
      {/* Redirects from old labour routes */}
      <Route
        path={AppRoutes.labour.itemsManage}
        element={<Navigate to={AppRoutes.labour.manage} replace />}
      />
      <Route
        path={AppRoutes.labour.groupsManage}
        element={<Navigate to={AppRoutes.labour.manage} replace />}
      />
      <Route
        path={AppRoutes.users.manage}
        element={
          <AuthenticatedRoute>
            <PermissionGate permission={Permissions.Users.Manage}>
              <UserManagementPage />
            </PermissionGate>
          </AuthenticatedRoute>
        }
      />
      <Route
        path={AppRoutes.users.offboarding}
        element={
          <AuthenticatedRoute>
            <PermissionGate permission={Permissions.Users.Manage}>
              <FeatureSwitchGate feature={Features.Offboarding.IsEnabled}>
                <OffboardingPage />
              </FeatureSwitchGate>
            </PermissionGate>
          </AuthenticatedRoute>
        }
      />
      <Route
        path={AppRoutes.users.detail}
        element={
          <AuthenticatedRoute>
            <PermissionGate permission={Permissions.Users.Manage}>
              <UserDetailPage />
            </PermissionGate>
          </AuthenticatedRoute>
        }
      />
      <Route
        path={AppRoutes.gangs.manage}
        element={
          <AuthenticatedRoute>
            <PermissionGate permission={Permissions.Gangs.Read}>
              <GangManagementPage />
            </PermissionGate>
          </AuthenticatedRoute>
        }
      />
      {/* Finance routes (formerly Pot Approvals) */}
      <Route
        path={AppRoutes.finance.pots}
        element={
          <AuthenticatedRoute>
            <PotApprovalsPage />
          </AuthenticatedRoute>
        }
      />
      <Route
        path={AppRoutes.finance.payments}
        element={
          <AuthenticatedRoute>
            <PermissionGate permission={Permissions.Pots.Pay}>
              <PaymentsPage />
            </PermissionGate>
          </AuthenticatedRoute>
        }
      />
      <Route
        path={AppRoutes.finance.deductions}
        element={
          <AuthenticatedRoute>
            <PermissionGate permission={Permissions.Deductions.All.Read}>
              <DeductionsPage />
            </PermissionGate>
          </AuthenticatedRoute>
        }
      />
      <Route
        path={AppRoutes.finance.potDetail}
        element={
          <AuthenticatedRoute>
            <PotDetailsPage />
          </AuthenticatedRoute>
        }
      />
      {/* Platform (internal admin) */}
      <Route
        path={AppRoutes.platform.organisations}
        element={
          <AuthenticatedRoute>
            <PermissionGate
              permission={Permissions.Platform.Organisations.Manage}
            >
              <PlatformOrganisationsPage />
            </PermissionGate>
          </AuthenticatedRoute>
        }
      />
      <Route
        path={AppRoutes.platform.organisationDetail}
        element={
          <AuthenticatedRoute>
            <PermissionGate
              permission={Permissions.Platform.Organisations.Manage}
            >
              <PlatformOrganisationDetailPage />
            </PermissionGate>
          </AuthenticatedRoute>
        }
      />
      <Route
        path={AppRoutes.platform.features}
        element={
          <AuthenticatedRoute>
            <PermissionGate permission={Permissions.Platform.Features.Read}>
              <PlatformFeatureSwitchesPage />
            </PermissionGate>
          </AuthenticatedRoute>
        }
      />
      {/* Developer Tools */}
      <Route
        path={AppRoutes.developer.notifications}
        element={
          <AuthenticatedRoute>
            <PermissionGate permission={Permissions.Developer.Access}>
              <NotificationTestingPage />
            </PermissionGate>
          </AuthenticatedRoute>
        }
      />
      {/* Design mockups — fake data only, no auth needed */}
      <Route path={AppRoutes.demo} element={<ListMockupsPage />} />
      {/* Redirects from old pot-approvals routes */}
      <Route
        path="/pot-approvals"
        element={<Navigate to={AppRoutes.finance.pots} replace />}
      />
      <Route
        path="/pot-approvals/:potId"
        element={<Navigate to={AppRoutes.finance.pots} replace />}
      />
      <Route path={AppRoutes.notFound} element={<NotFound />} />
    </RRDRoutes>
  );
};

export default AppRouter;
